const isServer = typeof window === "undefined";
const isDev = process.env.NODE_ENV !== "production";

/**
 * Lazy-loaded pino instance (server only).
 * Avoids bundling pino into client-side code.
 */
let _pino: any = null;

async function getPino() {
  if (!_pino) {
    const mod = await import("pino");
    const pinoFn = (mod.default ?? mod) as any;
    _pino = pinoFn({ level: isDev ? "debug" : "info" });
  }
  return _pino as { info: Function; warn: Function; error: Function; debug: Function };
}

type LogLevel = "info" | "warn" | "error" | "debug";

function consoleLog(level: LogLevel, message: string, data?: any) {
  const args = data !== undefined ? [message, data] : [message];
  if (level === "error") {
    console.error(...args);
  } else if (level === "warn") {
    console.warn(...args);
  } else if (level === "debug" && isDev) {
    console.debug(...args);
  } else {
    console.log(...args);
  }
}

function serverLog(level: LogLevel, context: string, message: string, data?: any) {
  getPino()
    .then((logger) => {
      const payload = data !== undefined ? { context, ...data } : { context };
      logger[level](payload, message);
    })
    .catch(() => consoleLog(level, message, data));
}

/**
 * Resolves args for overloaded signatures:
 *   (context, message, data?) → with context
 *   (message, data?)          → without context (client usage)
 */
function resolve(
  a: string,
  b?: any,
  c?: any,
): { context: string | null; message: string; data: any } {
  if (typeof b === "string") {
    return { context: a, message: b, data: c };
  }
  return { context: null, message: a, data: b };
}

function log(level: LogLevel, a: string, b?: any, c?: any) {
  const { context, message, data } = resolve(a, b, c);
  if (isServer && context) {
    serverLog(level, context, message, data);
  } else {
    consoleLog(level, message, data);
  }
}

/**
 * ULogger — static logging utility.
 *
 * Server:  uses pino with structured context field.
 * Client:  uses console without component name.
 *
 * Two call styles:
 *   ULogger.info("Context", "message", { extra })  ← with context (recommended for server code)
 *   ULogger.info("message", { extra })              ← without context (client components)
 */
export class ULogger {
  static info(context: string, message: string, data?: any): void;
  static info(message: string, data?: any): void;
  static info(a: string, b?: any, c?: any) {
    log("info", a, b, c);
  }

  static error(context: string, message: string, error?: any): void;
  static error(message: string, error?: any): void;
  static error(a: string, b?: any, c?: any) {
    log("error", a, b, c);
  }

  static warn(context: string, message: string, data?: any): void;
  static warn(message: string, data?: any): void;
  static warn(a: string, b?: any, c?: any) {
    log("warn", a, b, c);
  }

  static debug(context: string, message: string, data?: any): void;
  static debug(message: string, data?: any): void;
  static debug(a: string, b?: any, c?: any) {
    log("debug", a, b, c);
  }
}
